import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import StorageIcon from '@mui/icons-material/Storage';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchServices, controlService, type ServiceItem, type ServiceAction } from '../api/services';
import { usePermission } from '../hooks/usePermission';
import { getSocket } from '../api/socket';
import { LogDrawer } from '../components/services/LogDrawer';
import { useSnackbar } from '../context/SnackbarContext';

// ── Status chip ────────────────────────────────────────────────────────────

function StatusChip({ active }: { active: ServiceItem['active'] }) {
  const map: Record<ServiceItem['active'], { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    active:       { color: 'success', label: 'active' },
    inactive:     { color: 'default', label: 'inactive' },
    failed:       { color: 'error',   label: 'failed' },
    activating:   { color: 'warning', label: 'activating' },
    deactivating: { color: 'warning', label: 'deactivating' },
    unknown:      { color: 'default', label: 'unknown' },
  };
  const { color, label } = map[active] ?? { color: 'default', label: active };
  return <Chip label={label} color={color} size="small" />;
}

// ── Bulk state chip ────────────────────────────────────────────────────────

type BulkState = 'pending' | 'running' | 'ok' | 'failed';

function BulkStateChip({ state }: { state: BulkState }) {
  if (state === 'running') {
    return (
      <Chip
        label="running"
        color="info"
        size="small"
        icon={<CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />}
      />
    );
  }
  if (state === 'ok') return <Chip label="done" color="success" size="small" />;
  if (state === 'failed') return <Chip label="failed" color="error" size="small" />;
  return <Chip label="queued" size="small" />;
}

// ── Actions ────────────────────────────────────────────────────────────────

function ServiceActions({
  service,
  onAction,
  loading,
}: {
  service: ServiceItem;
  onAction: (id: string, action: ServiceAction) => void;
  loading: boolean;
}) {
  const canControl = usePermission(`services.${service.id}.control`);
  if (!canControl) return null;

  const isActive = service.active === 'active' || service.active === 'activating';

  return (
    <ButtonGroup size="small" disabled={loading}>
      {isActive ? (
        <Button color="warning" onClick={() => onAction(service.id, 'stop')}>Stop</Button>
      ) : (
        <Button color="success" onClick={() => onAction(service.id, 'start')}>Start</Button>
      )}
      <Button onClick={() => onAction(service.id, 'restart')}>Restart</Button>
      {service.enabled ? (
        <Button color="error" onClick={() => onAction(service.id, 'disable')}>Disable</Button>
      ) : (
        <Button onClick={() => onAction(service.id, 'enable')}>Enable</Button>
      )}
    </ButtonGroup>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function Services() {
  const canRead = usePermission('services.read');
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();

  const [drawerService, setDrawerService] = useState<ServiceItem | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkItemStates, setBulkItemStates] = useState<Record<string, BulkState>>({});
  const [bulkConfirm, setBulkConfirm] = useState<{ action: ServiceAction; ids: string[] } | null>(null);

  const isBulkRunning = Object.keys(bulkItemStates).length > 0;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices,
    enabled: canRead,
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (!canRead) return;
    const socket = getSocket();
    const handler = (items: ServiceItem[]) => {
      qc.setQueryData(['services'], { services: items });
    };
    socket.on('services:status', handler);
    return () => { socket.off('services:status', handler); };
  }, [canRead, qc]);

  const handleAction = async (id: string, action: ServiceAction) => {
    setActionLoading(id);
    try {
      await controlService(id, action);
      void qc.invalidateQueries({ queryKey: ['services'] });
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const services = data?.services ?? [];

  const allSelected = services.length > 0 && selected.size === services.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(services.map((s) => s.id)));
  };

  const executeBulk = async () => {
    if (!bulkConfirm) return;
    const { action, ids } = bulkConfirm;
    setBulkConfirm(null);

    const initial: Record<string, BulkState> = {};
    for (const id of ids) initial[id] = 'pending';
    setBulkItemStates(initial);

    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      setBulkItemStates((prev) => ({ ...prev, [id]: 'running' }));
      try {
        await controlService(id, action);
        setBulkItemStates((prev) => ({ ...prev, [id]: 'ok' }));
        ok++;
      } catch {
        setBulkItemStates((prev) => ({ ...prev, [id]: 'failed' }));
        fail++;
      }
    }

    setSelected(new Set());
    const verb = action === 'start' ? 'started' : action === 'stop' ? 'stopped' : 'restarted';
    showSnackbar(
      `${ok} of ${ids.length} ${verb}${fail > 0 ? `, ${fail} failed` : ''}`,
      fail > 0 ? 'warning' : 'success',
    );
    setTimeout(() => setBulkItemStates({}), 3_000);
    void qc.invalidateQueries({ queryKey: ['services'] });
  };

  if (!canRead) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="text.secondary">
          You do not have permission to view services.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Services</Typography>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load services.</Alert>
      )}

      {selected.size > 0 && !isBulkRunning && (
        <Paper sx={{ mb: 2, px: 2, py: 1, bgcolor: 'action.selected' }} elevation={0} variant="outlined">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ mr: 1 }}>{selected.size} selected</Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => setBulkConfirm({ action: 'start', ids: [...selected] })}
            >
              Start
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => setBulkConfirm({ action: 'stop', ids: [...selected] })}
            >
              Stop
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => setBulkConfirm({ action: 'restart', ids: [...selected] })}
            >
              Restart
            </Button>
            <Box flex={1} />
            <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
          </Stack>
        </Paper>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                    disabled={isBulkRunning || services.length === 0}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {services.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">
                      No services configured. Add entries to /etc/webadmin/services.yaml.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {services.map((svc) => (
                <TableRow
                  key={svc.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDrawerService(svc)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selected.has(svc.id)}
                      onChange={() => toggleSelect(svc.id)}
                      disabled={isBulkRunning}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{svc.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{svc.unit}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusChip active={svc.active} />
                      {bulkItemStates[svc.id] && (
                        <BulkStateChip state={bulkItemStates[svc.id]} />
                      )}
                      {actionLoading === svc.id && !isBulkRunning && (
                        <CircularProgress size={14} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={svc.enabled ? 'enabled' : 'disabled'}
                      size="small"
                      variant="outlined"
                      color={svc.enabled ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{svc.description}</Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ServiceActions
                      service={svc}
                      onAction={(id, action) => void handleAction(id, action)}
                      loading={actionLoading === svc.id || isBulkRunning}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="View logs">
                        <Button
                          size="small"
                          startIcon={<TerminalIcon />}
                          onClick={() => setDrawerService(svc)}
                        >
                          Logs
                        </Button>
                      </Tooltip>
                      {svc.id === 'mysql' && (
                        <Tooltip title="Open MySQL admin">
                          <IconButton size="small" component={RouterLink} to="/mysql">
                            <StorageIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={bulkConfirm !== null}
        onClose={() => setBulkConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: 'capitalize' }}>
          Bulk {bulkConfirm?.action}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {bulkConfirm?.action} {bulkConfirm?.ids.length} service(s):
          </Typography>
          <List dense disablePadding>
            {bulkConfirm?.ids.map((id) => (
              <ListItem key={id} disableGutters>
                <ListItemText primary={services.find((s) => s.id === id)?.name ?? id} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void executeBulk()}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <LogDrawer
        serviceId={drawerService?.id ?? null}
        serviceName={drawerService?.name ?? ''}
        unit={drawerService?.unit ?? null}
        onClose={() => setDrawerService(null)}
      />
    </Box>
  );
}
